/**
 * TK-45 §4 (lee 25/10, ảnh `y03b_vi_du_do_thi.png`) — ĐỒ THỊ ĐỘ SÁNG: mỗi nhóm một đường bậc thang, trục x = GIỜ ĐỒNG HỒ đi qua
 *   nửa đêm, chỉ vẽ trong khoảng On → Off theo LỊCH ĐÓNG CẮT của tủ cho ngày đang chọn. Thuần (không React, không mạng).
 *
 *   Mọi mốc tính bằng PHÚT TUYỆT ĐỐI kể từ 00:00 của ngày đang chọn D: 18:00 hôm ấy = 1080, 01:00 hôm sau = 1500.
 *   · On = lúc ngõ bật buổi tối ngày D; Off = lúc ngõ tắt (thường sáng D+1).
 *   · Từ On tới timer đầu tiên của nhóm TRONG ĐÊM ẤY: 100 % (đèn vừa có điện chạy 100 %).
 *   · Timer đổi mức khi tới giờ, xét "thứ" của timer: timer trước nửa đêm thuộc ngày D, sau nửa đêm thuộc ngày D+1.
 *   · Ngoài On–Off: không vẽ (đèn không có điện).
 *   ⚠ Luật "seek" khi SLCU khởi động giữa đêm chưa chốt — đồ thị này vẽ theo hình lee (lúc On = 100 %), không theo seek.
 */
import { daiCapDien, PHUT_NGAY, type MatTroi, type NgayVN, type NgoLich } from './gioCapDien';

export const NHOM_TAT = 255;
export const NHOM_TOI_DA = 254;
const coThu = (thu: number, i: number) => ((thu >> i) & 1) === 1;

export interface TimerDoThi { so: number; gio_phut: number; muc: number; thu: number; nhom: number }
export interface Bac { tu: number; den: number; muc: number }
export interface DuongNhom { nhom: number; bac: Bac[]; diem: { so: number; luc: number; muc: number }[] }

/**
 * Các bậc của MỘT nhóm trong đêm của ngày `thu` (0 = CN … 6 = T7), giữa `on` và `off` (phút tuyệt đối, off > on).
 * Nhóm không có timer nào chạy trong đêm ấy ⇒ một bậc 100 % suốt On–Off.
 */
export function bacNhom(timers: TimerDoThi[], nhom: number, thu: number, on: number, off: number): DuongNhom {
  const sau = (thu + 1) % 7;
  const su: { so: number; luc: number; muc: number }[] = [];
  for (const t of timers) {
    if (t.nhom !== nhom) continue;
    /* timer ngày D (trước nửa đêm) và timer ngày D+1 (sau nửa đêm) — cùng một timer có thể chạy cả hai */
    if (coThu(t.thu, thu) && t.gio_phut >= on && t.gio_phut < off) su.push({ so: t.so, luc: t.gio_phut, muc: t.muc });
    const l = t.gio_phut + PHUT_NGAY;
    if (coThu(t.thu, sau) && l >= on && l < off) su.push({ so: t.so, luc: l, muc: t.muc });
  }
  su.sort((a, b) => a.luc - b.luc || a.so - b.so);
  const bac: Bac[] = [];
  let tu = on, muc = 100;
  for (const e of su) {
    if (e.luc > tu) bac.push({ tu, den: e.luc, muc });
    tu = e.luc; muc = e.muc;
  }
  if (off > tu) bac.push({ tu, den: off, muc });
  /* gộp hai bậc liền nhau cùng mức (timer đặt lại đúng mức đang chạy) */
  const gon: Bac[] = [];
  for (const b of bac) { const c = gon[gon.length - 1]; if (c && c.den === b.tu && c.muc === b.muc) c.den = b.den; else gon.push({ ...b }); }
  return { nhom, bac: gon, diem: su };
}

/** Nhóm có ít nhất một timer đang dùng, tăng dần. */
export const nhomDangDung = (timers: TimerDoThi[]) => [...new Set(timers.filter((t) => t.nhom !== NHOM_TAT).map((t) => t.nhom))].sort((a, b) => a - b);

export interface OnOffNgo { index: number; on: number; off: number }
export type KetOnOff =
  | { loai: 'chua-biet' }                                                 // tủ chưa báo lịch / lịch theo hoàng hôn mà chưa có giờ — luật số 0
  | { loai: 'chua-co' }                                                   // tủ có lịch nhưng không ngõ nào bật đêm ấy
  | { loai: 'co'; ngo: OnOffNgo[]; chung: boolean; on: number; off: number; truc: [number, number] };

/**
 * On / Off từng ngõ cho đêm của ngày `n` (dải cấp điện ngày D và D+1, cùng hàm `daiCapDien` với mọi chỗ khác).
 *   · On = đầu đoạn CUỐI của ngày D; đoạn ấy chạm 24:00 và ngày D+1 bắt đầu lúc 00:00 ⇒ Off = cuối đoạn đầu D+1 (+ 1440).
 *   · Ngõ chạy cả hai ngày trọn 24 h ("luôn bật") ⇒ On / Off = mép trục (12:00 D → 12:00 D+1).
 *   · Các ngõ cùng giờ ⇒ `chung`; khác giờ ⇒ đường của nhóm dùng khoảng RỘNG NHẤT (On sớm nhất → Off muộn nhất).
 */
export function onOffDem(lich: NgoLich[] | null, n: NgayVN, n1: NgayVN, mt: MatTroi): KetOnOff {
  if (!lich) return { loai: 'chua-biet' };
  if (lich.length === 0) return { loai: 'chua-co' };
  const ngo: OnOffNgo[] = [];
  let luonBat = false;
  for (const o of lich) {
    if (!o.enabled) continue;
    const a = daiCapDien(lich, [o.index], n, mt), b = daiCapDien(lich, [o.index], n1, mt);
    if (a === null || b === null) return { loai: 'chua-biet' };     // lịch theo hoàng hôn mà tủ chưa báo giờ ⇒ không đoán
    if (a.length === 0) continue;
    const cuoi = a[a.length - 1];
    if (cuoi[0] === 0 && cuoi[1] === PHUT_NGAY && b.length && b[0][0] === 0 && b[0][1] === PHUT_NGAY) { luonBat = true; continue; }
    const off = cuoi[1] === PHUT_NGAY && b.length && b[0][0] === 0 ? PHUT_NGAY + b[0][1] : cuoi[1];
    ngo.push({ index: o.index, on: cuoi[0], off });
  }
  if (ngo.length === 0 && !luonBat) return { loai: 'chua-co' };
  if (ngo.length === 0) return { loai: 'co', ngo: [], chung: true, on: 720, off: 2160, truc: [720, 2160] };
  const on = Math.min(...ngo.map((g) => g.on)), off = Math.max(...ngo.map((g) => g.off));
  const chung = ngo.every((g) => g.on === ngo[0].on && g.off === ngo[0].off);
  if (luonBat) return { loai: 'co', ngo, chung, on: 720, off: 2160, truc: [720, 2160] };
  return { loai: 'co', ngo, chung, on, off, truc: [Math.floor((on - 240) / 60) * 60, Math.ceil((off + 240) / 60) * 60] };
}

/**
 * Khoảng On → Off của MỘT nhóm: theo NGÕ CỦA NHÁNH đèn trong nhóm (câu hỏi TK-36 "theo ngõ của đích"), rộng nhất trong các ngõ ấy;
 * nhóm chưa gán nhánh / nhánh không có trong lịch ⇒ mọi ngõ (khoảng chung).
 */
export function khoangNhom(oo: Extract<KetOnOff, { loai: 'co' }>, chiSo: readonly number[]): { on: number; off: number } {
  const g = chiSo.length ? oo.ngo.filter((x) => chiSo.includes(x.index)) : [];
  return g.length ? { on: Math.min(...g.map((x) => x.on)), off: Math.max(...g.map((x) => x.off)) } : { on: oo.on, off: oo.off };
}

/** Cả đồ thị: On/Off + mỗi nhóm đang dùng một đường (trong khoảng của ngõ nhóm ấy). Lịch chưa biết / chưa có ⇒ không đường nào. */
export function doThiDim(timers: TimerDoThi[], thu: number, oo: KetOnOff, ngoCua: (nhom: number) => readonly number[] = () => []): { oo: KetOnOff; duong: DuongNhom[] } {
  if (oo.loai !== 'co') return { oo, duong: [] };
  return { oo, duong: nhomDangDung(timers).map((so) => { const k = khoangNhom(oo, ngoCua(so)); return bacNhom(timers, so, thu, k.on, k.off); }) };
}

/**
 * Bảng màu PHÂN LOẠI cho nhóm (TK-45 §4) — KHÔNG dùng màu trạng thái (`tot` xanh lá · `nguy` đỏ · `canh` cam). Ví dụ đỏ / xanh lá
 * trong ảnh của lee chỉ để minh hoạ. Màu theo SỐ nhóm (ổn định khi đổi ngày / thêm nhóm).
 */
export const MAU_NHOM = ['#4f8ef7', '#a472f2', '#2fb7cf', '#e56bb0', '#8e9bff', '#c9b79c'] as const;
export const mauNhomSo = (so: number) => MAU_NHOM[((so % MAU_NHOM.length) + MAU_NHOM.length) % MAU_NHOM.length];

/** Ô Nhóm của Timer dim (TK-45 §3): chuỗi người gõ ⇒ số nhóm 0–254, hoặc null (trống / ngoài khoảng / không phải số nguyên). */
export function docNhom(s: string): number | null {
  const t = s.trim();
  if (!/^\d{1,3}$/.test(t)) return null;
  const n = Number(t);
  return n >= 0 && n <= NHOM_TOI_DA ? n : null;
}
/** Bỏ số 0 đứng đầu khi hiện ("050" ⇒ "50", "0" giữ). */
export const boSo0 = (s: string) => s.replace(/^0+(?=\d)/, '');

/** "HH:MM" cho phút tuyệt đối (qua nửa đêm vẫn ra giờ đồng hồ). */
export const gioDongHo = (p: number) => { const m = ((p % PHUT_NGAY) + PHUT_NGAY) % PHUT_NGAY; return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`; };
